
import React, { useState } from 'react';
import { Language, AccessToken } from '../../types';

interface ProfileKeyModalProps {
  language: Language;
  onClose: () => void;
  onCreate: (name: string, type: AccessToken['type'], durationDays: number) => void;
}

const ProfileKeyModal: React.FC<ProfileKeyModalProps> = ({ language, onClose, onCreate }) => {
  const isRtl = language === 'ar';
  const [name, setName] = useState('');
  const [type, setType] = useState<AccessToken['type']>('standard');
  const [duration, setDuration] = useState<number>(30); // days, -1 for never

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onCreate(name, type, duration);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm animate-in fade-in" onClick={onClose}></div>
      <div className="relative w-full max-w-md bg-white dark:bg-[#15151a] rounded-[32px] shadow-2xl border border-slate-200 dark:border-white/10 overflow-hidden animate-in zoom-in-95 duration-300">
        <div className="p-6 border-b border-slate-100 dark:border-white/5 flex justify-between items-center">
          <h3 className="text-lg font-black text-slate-800 dark:text-white">{isRtl ? 'إنشاء مفتاح وصول جديد' : 'Create New Access Key'}</h3>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full bg-slate-100 dark:bg-white/5 hover:bg-rose-500 hover:text-white transition-all text-slate-400"><i className="fas fa-times"></i></button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          <div className="space-y-1.5">
            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{isRtl ? 'اسم المفتاح (للتوضيح)' : 'Key Label'}</label>
            <input 
              type="text" 
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={isRtl ? "مثال: مفتاح تطوير الويب" : "e.g., Web Dev Key"}
              className="w-full bg-slate-50 dark:bg-black/20 border border-slate-200 dark:border-white/10 rounded-xl px-4 py-3 text-sm font-bold dark:text-white outline-none focus:border-emerald-500 transition-colors"
            />
          </div>

          <div className="space-y-1.5">
            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{isRtl ? 'نوع الصلاحية' : 'Permission Type'}</label>
            <div className="grid grid-cols-3 gap-2">
               {(['read_only', 'standard', 'admin'] as const).map(t => (
                 <button 
                   key={t}
                   type="button"
                   onClick={() => setType(t)}
                   className={`py-2 rounded-lg text-[10px] font-black uppercase transition-all ${type === t ? 'bg-emerald-500 text-white shadow-md' : 'bg-slate-100 dark:bg-white/5 text-slate-500 hover:bg-slate-200 dark:hover:bg-white/10'}`}
                 >
                   {t.replace('_', ' ')}
                 </button>
               ))}
            </div>
          </div>

          <div className="space-y-1.5">
            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{isRtl ? 'مدة الصلاحية' : 'Expiration'}</label>
            <select 
              value={duration}
              onChange={(e) => setDuration(Number(e.target.value))}
              className="w-full bg-slate-50 dark:bg-black/20 border border-slate-200 dark:border-white/10 rounded-xl px-4 py-3 text-sm font-bold dark:text-white outline-none focus:border-emerald-500 transition-colors"
            >
              <option value={7}>{isRtl ? '7 أيام' : '7 Days'}</option>
              <option value={30}>{isRtl ? '30 يوم' : '30 Days'}</option>
              <option value={90}>{isRtl ? '90 يوم' : '90 Days'}</option>
              <option value={365}>{isRtl ? 'سنة واحدة' : '1 Year'}</option>
              <option value={-1}>{isRtl ? 'لا تنتهي أبداً' : 'Never Expires'}</option>
            </select>
          </div>

          <button type="submit" className="w-full py-4 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white font-black text-sm shadow-lg shadow-emerald-500/20 active:scale-[0.98] transition-all flex items-center justify-center gap-2 mt-4">
             <i className="fas fa-key"></i>
             {isRtl ? 'إنشاء المفتاح' : 'Generate Key'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ProfileKeyModal;
